import { useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { getConfig } from './api/client'
import { AuthProvider } from './auth'
import { CartProvider } from './cart'
import { Layout } from './components/Layout'
import { Meldungen } from './components/Meldung'
import { useResource } from './hooks/useResource'
import { useT } from './i18n'
import { CheckoutPage } from './pages/CheckoutPage'
import { CollectionPage } from './pages/CollectionPage'
import { CollectionsPage } from './pages/CollectionsPage'
import { HomePage } from './pages/HomePage'
import { MyOrdersPage } from './pages/MyOrdersPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { OrderPage } from './pages/OrderPage'
import { ProductPage } from './pages/ProductPage'
import { SearchPage } from './pages/SearchPage'
import { TextPage } from './pages/TextPage'
import { ShopConfigProvider } from './shopConfig'
import { ShopPagesProvider } from './shopPages'

/**
 * Beim Seitenwechsel nach oben.
 *
 * Ohne das behält der Browser die alte Scrollhöhe: Wer unten in einer langen
 * Liste ein Produkt öffnet, landet in dessen Beschreibung statt an seinem
 * Namen und hält die Seite für halb geladen.
 */
function NachObenBeiWechsel() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [pathname])
  return null
}

export function App() {
  const t = useT()

  // Die Ladenangaben sind die einzige Anfrage vor allem anderen: Ohne Name und
  // Währung ließe sich kein Kopf und kein Preis anzeigen, und ein kurz
  // aufblitzender Platzhaltername sähe aus wie der falsche Laden.
  const { data: config, error, loading } = useResource((signal) => getConfig(signal), [])

  if (loading) {
    return (
      <div className="seite">
        <main className="inhalt">
          <p className="hinweis" role="status">
            {t('app.oeffnet')}
          </p>
        </main>
      </div>
    )
  }

  if (error != null || !config) {
    return (
      <div className="seite">
        <main className="inhalt">
          <h1>{t('app.fehlerTitel')}</h1>
          <p className="beschreibung">{t('app.fehlerText')}</p>
        </main>
      </div>
    )
  }

  return (
    <ShopConfigProvider config={config}>
      {/* Die Seiten des Ladens einmal für alle: Der Fußbereich verlinkt sie,
          und die Kasse fragt, ob es eine Widerrufsbelehrung gibt. */}
      <ShopPagesProvider>
        {/* Der Warenkorb steht über allen Seiten: Er überlebt den Seitenwechsel,
            und das Symbol in der Kopfzeile zeigt überall dieselbe Zahl. Er
            liegt auf dem Server; hier steht nur die zuletzt gelieferte Ansicht.

            Die Anmeldung liegt innerhalb, nicht außerhalb: Wer sich abmeldet,
            bekommt einen anderen Warenkorb — und das Abmelden muss ihn
            nachladen können. */}
        {/* Die Meldungen liegen um alles, was welche absetzt — Warenkorb,
            Kasse, Bestellansicht. */}
        <Meldungen>
        <CartProvider>
          <AuthProvider>
          <NachObenBeiWechsel />
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/sammlung" element={<CollectionsPage />} />
              {/* Sprechende Adressen mit dem Handle aus dem Backoffice: /sammlung/getraenke.
                  Keine Kennung in der Adresse — weder die des Produkts noch die des Ladens. */}
              <Route path="/sammlung/:handle" element={<CollectionPage />} />
              <Route path="/produkt/:handle" element={<ProductPage />} />
              <Route path="/suche" element={<SearchPage />} />
              <Route path="/kasse" element={<CheckoutPage />} />
              {/* Zwei Adressen, eine Seite: die frische Bestätigung mit Nummer
                  und die spätere Nachschau. Die E-Mail steht in keiner von beiden. */}
              <Route path="/bestellung" element={<OrderPage />} />
              <Route path="/bestellung/:nummer" element={<OrderPage />} />
              {/* Die Bestellungen des Angemeldeten — der eine Grund, aus dem
                  sich in einem Laden ohne Kontozwang jemand anmeldet. */}
              <Route path="/meine-bestellungen" element={<MyOrdersPage />} />
              {/* Impressum, Widerruf, Datenschutz und was der Laden sonst
                  schreibt — eine Route für alle, den Rest bestimmt das Handle. */}
              <Route path="/seite/:handle" element={<TextPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
          </AuthProvider>
        </CartProvider>
        </Meldungen>
      </ShopPagesProvider>
    </ShopConfigProvider>
  )
}
